/**
 * Feature Flags for ASM Toolkit
 *
 * Controls visibility of features in the UI. Features that are disabled:
 * - Will not appear in navigation
 * - Routes will redirect to home
 *
 * To enable a feature:
 * 1. Set the flag to `true` in this file, OR
 * 2. Set the environment variable (e.g., VITE_FEATURE_ATTACK_PATHS=true)
 *
 * This allows gradual rollout and per-customer enablement.
 */

// Read feature flags from environment variables with defaults
const env = import.meta.env;

/**
 * Feature flag definitions
 *
 * MVP Launch: Some advanced features are hidden by default to reduce
 * cognitive load for new users. These can be enabled when the feature
 * is stable and properly documented.
 */
export const FEATURES = {
  /**
   * Scan comparisons: Compare two scans side-by-side
   * Status: ENABLED - core to continuous security monitoring
   */
  comparisons: env.VITE_FEATURE_COMPARISONS !== "false", // Enabled by default

  /**
   * Attack Paths: Visualize potential attack chains
   * Status: Hidden for MVP - complex visualization, needs user education
   */
  attackPaths: env.VITE_FEATURE_ATTACK_PATHS === "true" || false,

  /**
   * Graph Visualization: Asset topology graph
   * Status: Hidden for MVP - advanced feature, adds cognitive load
   */
  graphVisualization: env.VITE_FEATURE_GRAPH === "true" || false,

  /**
   * Service Matrix: Service exposure heatmap
   * Status: Hidden for MVP - advanced view, similar to risks
   */
  serviceMatrix: env.VITE_FEATURE_MATRIX === "true" || false,

  /**
   * Timeline: Asset changes over time
   * Status: ENABLED - simple, valuable feature
   */
  timeline: env.VITE_FEATURE_TIMELINE !== "false", // Enabled by default

  /**
   * Asset Inventory: Core asset management
   * Status: ENABLED - core feature
   */
  assetInventory: true, // Always enabled

  /**
   * Risk Dashboard: Security risks overview
   * Status: ENABLED - high value, simple UI
   */
  riskDashboard: env.VITE_FEATURE_RISKS !== "false", // Enabled by default

  /**
   * Scheduled Scans: Recurring scans
   * Status: ENABLED - enterprise expectation
   */
  schedules: true, // Always enabled

  /**
   * Webhooks: Notification webhooks
   * Status: ENABLED - integration capability
   */
  webhooks: true, // Always enabled
} as const;

export type FeatureFlag = keyof typeof FEATURES;

/**
 * Check if a feature is enabled
 */
export function isFeatureEnabled(feature: FeatureFlag): boolean {
  return FEATURES[feature];
}

/**
 * Hook for feature flag checks in components
 */
export function useFeature(feature: FeatureFlag): boolean {
  return FEATURES[feature];
}

/**
 * Filter an array based on feature flags
 * Useful for navigation items, tabs, etc.
 */
export function filterByFeature<T extends { feature?: FeatureFlag }>(
  items: T[]
): T[] {
  return items.filter((item) => !item.feature || isFeatureEnabled(item.feature));
}
